import { ErrorAlert } from "@components/common/ErrorAlert";
import { SkeletonLoader } from "@components/common/SkeletonLoader";
import CustomMap from "@components/Map";
import type { Mission } from "@components/Map/type";
import { useUserContext } from "@contexts/UserContext/index.context";
import { Box } from "@mui/material";
import { DeliveryStatus, DeliveryType } from "@types_def/models/delivery.types";
import { UserRole } from "@types_def/models/user.type";
import { memo } from "react";
import uniqolor from "uniqolor";

import { useFindAllDeliveryQuery } from "@/libs/generated/hooks";

function toLatLngLiteral(latitude?: number | null, longitude?: number | null) {
  if (
    latitude === null ||
    latitude === undefined ||
    longitude === null ||
    longitude === undefined
  ) {
    return null;
  }

  return {
    lat: latitude,
    lng: longitude,
  };
}

function DeliveryMap() {
  const role = useUserContext().userState?.["custom:role"]?.toString();
  const slug = useUserContext().userState?.organizationId;

  const filter =
    role === UserRole.ADMIN
      ? { status: DeliveryStatus.ONGOING }
      : {
          status: DeliveryStatus.ONGOING,
          organization: {
            slug,
          },
        };

  const { data, isLoading, error, refetch } = useFindAllDeliveryQuery(
    {
      GetAllInput: {
        pagination: { page: 0, perPage: 1000 },
        filter,
        include: {
          fromAddress: true,
          toAddress: true,
          toReturnToAddress: true,
          selectedDriver: true,
        },
      },
    },
    {
      select: (response) => {
        const delivery = response.findAllDelivery.records;
        const data: Mission[] = delivery.flatMap((delivery) => {
          const fromAddressCord = toLatLngLiteral(
            delivery.fromAddress?.latitude,
            delivery.fromAddress?.longitude
          );
          const toAddressCord = toLatLngLiteral(
            delivery.toAddress?.latitude,
            delivery.toAddress?.longitude
          );
          const resToAddressCord =
            delivery.type === DeliveryType.TWO_WAY_DELIVERY
              ? toLatLngLiteral(
                  delivery.toReturnToAddress?.latitude,
                  delivery.toReturnToAddress?.longitude
                )
              : toAddressCord;

          if (fromAddressCord === null || toAddressCord === null || resToAddressCord === null) {
            return [];
          }

          const color = uniqolor(String(delivery.id), {
            differencePoint: 40,
          });
          const mission: Mission = {
            origin: fromAddressCord,
            destination: toAddressCord,
            toReturn: resToAddressCord,
            id: delivery.id.toString(),
            color: color.color,
            depart: delivery.fromAddress?.address ?? "",
            arrive: delivery.toAddress?.address ?? "",
            restitution:
              delivery.type === DeliveryType.TWO_WAY_DELIVERY
                ? (delivery.toReturnToAddress?.address ?? "")
                : "",
            driver: delivery.selectedDriver
              ? {
                  id: String(delivery.selectedDriver.id),
                  name:
                    delivery.selectedDriver.fullName ??
                    `${delivery.selectedDriver.firstName} ${delivery.selectedDriver.lastName}`,
                  avatar: delivery.selectedDriver.avatar ?? "",
                }
              : undefined,
            status: delivery.status,
            type: delivery.type,
          };
          return [mission];
        });
        return data;
      },
      refetchInterval: Infinity,
    }
  );

  return (
    <Box
      sx={{
        height: "calc(100vh - 75px - 85px)",
        width: "100%",
        position: "relative",
      }}
    >
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1000,
          }}
        >
          <SkeletonLoader count={5} type="card" />
        </Box>
      )}

      {error != null && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1000,
            maxWidth: 400,
          }}
        >
          <ErrorAlert
            error={error}
            onRetry={() => {
              void refetch();
            }}
          />
        </Box>
      )}

      {!isLoading && error == null && data != null && (
        <CustomMap data={data} liveTracking newDelivery={false} />
      )}
    </Box>
  );
}

export default memo(DeliveryMap);
